// The ONE place a schema-introspection request crosses the IPC bridge
// (SLICES.md § S7 items 2 + 4 + 7). Every paged fetcher of every engine routes
// through here so the per-page `maxRows` cap, the per-connection concurrency
// gate (`introspectionSemaphore.ts`) and the total-budget truncation report
// exist once rather than once per engine.
//
// Engine-neutral by construction: `mssqlIntrospectionQuery.ts` is a thin
// mssql-labelled wrapper over these functions, and pg / mysql / oracle /
// sqlite call them directly (S7 item 8).

import {
  INTROSPECTION_PAGE_MAX_ROWS,
  fetchIntrospectionPaged,
  limitOffsetClause,
  offsetFetchClause,
  type IntrospectionPageResult,
  type IntrospectionRow,
  type PagedIntrospectionOutcome,
} from './introspectionPaging';
import { runIntrospectionQuery } from './introspectionSemaphore';
import {
  reportIntrospectionFailure,
  reportIntrospectionTruncation,
  type IntrospectionTruncationEngine,
  type TruncationArtefact,
} from './introspectionTruncation';

/**
 * The rejection a paged fetch produces when one of its page queries fails.
 * Every cache wraps its fetchers in a catch that names the artefact in the
 * `feature:schema_snapshot_partial` event; before this the paging layer's
 * `failed` outcome was dropped by the void-returning wrappers, so a lost
 * connection or a permission error produced a "complete" empty snapshot
 * (release review 1.5.0, F3).
 */
export function introspectionFailureError(artefact: TruncationArtefact, message?: string): Error {
  return new Error(
    `Schema introspection failed while loading ${artefact}${message ? `: ${message}` : ''}`,
  );
}

/**
 * Options every paged fetcher accepts. `shouldAbort` is how a cache stops a
 * phase-2 fetch whose snapshot has been superseded (a newer refresh, a DDL
 * drop) instead of paging a catalog nobody will merge.
 */
export type PagedFetchOptions = {
  readonly shouldAbort?: () => boolean;
};

/** How an engine spells its page window. */
export type PagingDialect = 'offset-fetch' | 'limit-offset';

/** Build the page tail for `dialect`. Values are integer-coerced in the clause builders. */
export function pageClauseFor(
  dialect: PagingDialect,
  page: { offset: number; limit: number },
): string {
  return dialect === 'limit-offset' ? limitOffsetClause(page) : offsetFetchClause(page);
}

/**
 * Issue ONE introspection query through the connection's introspection lane.
 * Used for a page of a paged artefact AND for the O(1) probes (server
 * version, `search_path`, session variables) that have no paging to do but
 * must still queue behind the same two-slot gate as everything else.
 */
export async function fetchIntrospectionPage(args: {
  readonly engine: IntrospectionTruncationEngine;
  readonly connectionId: string;
  /** The `database` argument of `executeQueryV2` — Oracle passes its schema. */
  readonly database: string;
  /** Short slug for the executionId (`cols`, `fks`, `indexes`, …). */
  readonly label: string;
  readonly sql: string;
}): Promise<IntrospectionPageResult> {
  const executeQueryV2 = window.electronAPI?.executeQueryV2;
  if (!executeQueryV2) return { ok: false, rows: [], error: 'Electron bridge unavailable' };
  return runIntrospectionQuery(args.connectionId, async () => {
    const result = await executeQueryV2({
      connectionId: args.connectionId,
      database: args.database,
      sql: args.sql,
      executionId: `${args.engine}-ls-${args.label}-${args.connectionId}-${args.database}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      options: { maxRows: INTROSPECTION_PAGE_MAX_ROWS },
    });
    if (!result.success) return { ok: false, rows: [], error: result.error?.message };
    return {
      ok: true,
      rows: (result.resultSets?.[0]?.rows ?? []) as readonly IntrospectionRow[],
      truncated: result.stats?.truncated === true,
    };
  });
}

/**
 * Run one un-paged introspection probe through the lane and hand back its
 * rows. `[]` when the query failed — every probe in the tiered load degrades
 * to a default rather than failing the snapshot.
 *
 * Only for genuinely O(1)/small-fixed results (a version banner, `SHOW
 * search_path`, `PRAGMA database_list`, the installed-extension list). Any
 * catalog whose size scales with the schema goes through
 * {@link fetchPagedArtefact} instead.
 */
export async function fetchIntrospectionProbeRows(args: {
  readonly engine: IntrospectionTruncationEngine;
  readonly connectionId: string;
  readonly database: string;
  readonly label: string;
  readonly sql: string;
}): Promise<readonly IntrospectionRow[]> {
  const page = await fetchIntrospectionPage(args);
  if (!page.ok) return [];
  // A probe is only used where the result set cannot plausibly exceed one
  // page, so this branch is defensive. It reports WITHOUT an artefact on
  // purpose: the closed artefact vocabulary describes the bulk catalogs, and
  // inventing a bucket for `pg_extension` would put a wrong noun in the pill.
  // "Something in the schema list was clipped" is the honest answer here.
  if (page.truncated) {
    reportIntrospectionTruncation({
      connectionId: args.connectionId,
      database: args.database,
      engine: args.engine,
      cap: INTROSPECTION_PAGE_MAX_ROWS,
    });
  }
  return page.rows;
}

/**
 * Page one introspection artefact end to end: build each page's SQL from
 * `sqlForPage(<page clause>)`, aggregate + sink it, yield between pages, and
 * report the truncation (naming the artefact) if the TOTAL row budget is spent
 * before the server runs out of rows.
 *
 * REJECTS when a page query fails (after the pages that did land were sunk):
 * the failure is recorded for the pill and the caller's catch names the
 * artefact in the partial-snapshot event. Resolves with the outcome otherwise
 * — callers use {@link isCompleteOutcome} to decide whether untouched
 * carried-over metadata may be reconciled away.
 */
export async function fetchPagedArtefact<TItem>(args: {
  readonly engine: IntrospectionTruncationEngine;
  readonly connectionId: string;
  readonly database: string;
  readonly label: string;
  readonly artefact: TruncationArtefact;
  readonly dialect: PagingDialect;
  readonly sqlForPage: (pageClause: string) => string;
  readonly aggregate: (rows: readonly IntrospectionRow[]) => TItem[];
  readonly sink: (items: readonly TItem[]) => void | Promise<void>;
  readonly totalBudget: number;
  readonly groupKeyOf?: (row: IntrospectionRow) => string;
  readonly shouldAbort?: () => boolean;
}): Promise<PagedIntrospectionOutcome> {
  const outcome = await fetchIntrospectionPaged<TItem>({
    fetchPage: (page) =>
      fetchIntrospectionPage({
        engine: args.engine,
        connectionId: args.connectionId,
        database: args.database,
        label: args.label,
        sql: args.sqlForPage(pageClauseFor(args.dialect, page)),
      }),
    aggregate: args.aggregate,
    sink: args.sink,
    totalBudget: args.totalBudget,
    groupKeyOf: args.groupKeyOf,
    shouldAbort: args.shouldAbort,
  });
  // An aborted fetch belongs to a superseded load: nothing it observed may be
  // recorded against the scope the CURRENT load owns (round 3 N2).
  if (outcome.aborted) return outcome;
  if (outcome.capHit) {
    reportIntrospectionTruncation({
      connectionId: args.connectionId,
      database: args.database,
      engine: args.engine,
      cap: args.totalBudget,
      artefact: args.artefact,
    });
  }
  if (outcome.failed) {
    reportIntrospectionFailure({
      connectionId: args.connectionId,
      database: args.database,
      artefact: args.artefact,
      message: outcome.error,
    });
    throw introspectionFailureError(args.artefact, outcome.error);
  }
  return outcome;
}
